<script lang="ts">
  interface Props {
    onBack?: () => void;
  }
  let { onBack }: Props = $props();
</script>

<section aria-label="Personvern og hvordan det virker">
  {#if onBack}
    <div class="row" style="margin-bottom: 1rem;">
      <button type="button" onclick={onBack}>← Tilbake</button>
    </div>
  {/if}

  <h2 style="font-size: 1.75rem; margin-top: 0;">Personvern og hvordan det virker</h2>

  <p>
    Vinterliste lar deg lage og dele lister over ting å gjøre om vinteren —
    eller hva enn du vil bruke det til. Du velger selv om hver oppføring er
    privat, anonym eller offentlig. Her er hva det betyr i praksis, og hva
    serveren faktisk ser av det du skriver.
  </p>

  <h3>De fire synlighetsnivåene</h3>
  <ul>
    <li>
      <strong>Privat:</strong> oppføringen krypteres i nettleseren din før den
      sendes. Verken vi som driver tjenesten, eller noen som måtte få tak i
      databasen, kan lese innholdet. Bare du kan låse den opp — med passordet
      ditt eller gjenopprettingskoden.
    </li>
    <li>
      <strong>Venner:</strong> kun synlig for brukere du har lagt til som
      venner. Dette er en <em>tilgangsliste</em>, ikke kryptografi — innholdet
      lagres i klartekst hos oss, men vi serverer det bare til de du har valgt.
      Det betyr at vi (eller noen med databasetilgang) i prinsippet kan lese
      det, til forskjell fra «privat». Velg «privat» om innholdet er sensitivt.
    </li>
    <li>
      <strong>Anonym (halv-offentlig):</strong> innholdet er synlig for alle,
      men navnet ditt vises ikke ved siden av. Vi lagrer en intern referanse
      til hvem som eier oppføringen (slik at du kan redigere eller slette den),
      men API-et returnerer aldri den referansen til andre brukere.
    </li>
    <li>
      <strong>Offentlig:</strong> innholdet er synlig for alle <em>og</em>
      kreditert med visningsnavnet ditt (eller brukernavnet, hvis du har
      satt det). Setter du ingen av delene, vises ingen attribusjon.
    </li>
  </ul>

  <h3>Venner og blokkering</h3>
  <p>
    Vennskap i Vinterliste er enveis. Du kan legge til en bruker som venn
    uten at de må godkjenne — det er din egen «hvem får se mine oppføringer
    for venner»-liste. Den du legger til, ser i sin tur at du har lagt dem
    til, og kan velge å gjøre det samme andre veien om de vil.
  </p>
  <p>
    Brukere må ha satt opp et brukernavn (URL-vennlig kortform) for å være
    søkbare. Vi bruker brukernavnet for å finne hverandre — ikke eposten, som
    er en kontaktidentifikator vi ikke deler.
  </p>
  <p>
    Hvis noen har lagt deg til som venn, og du heller vil slippe det, kan du
    <strong>blokkere</strong> dem. Blokkering virker symmetrisk på innhold
    for venner: når én av dere har blokkert den andre, ser ingen av dere
    lenger oppføringer for venner fra den andre. Blokkering påvirker ikke
    offentlige eller anonyme oppføringer — det som er åpent for alle, er
    åpent for alle.
  </p>

  <h3>Ende-til-ende-kryptering, kort forklart</h3>
  <p>
    For private oppføringer skjer all kryptering i nettleseren din. Når du
    oppretter en konto, lager nettleseren en tilfeldig nøkkel som vi kaller
    DEK (Data Encryption Key). Denne nøkkelen pakkes inn — krypteres — på
    to måter:
  </p>
  <ul>
    <li>
      Én pakke åpnes med passordet ditt: nettleseren utleder en
      passordavledet nøkkel (KEK_pw) ved hjelp av <strong>Argon2id</strong>,
      en moderne nøkkelutledningsfunksjon som er treg med vilje for å gjøre
      brute-force-angrep upraktiske.
    </li>
    <li>
      Én pakke åpnes med gjenopprettingskoden: en høyentropi-kode som vises
      én gang ved registrering og som du må ta vare på selv. Vi lagrer den
      <strong>aldri</strong> på serveren.
    </li>
  </ul>
  <p>
    Selve innholdet krypteres med <strong>XChaCha20-Poly1305</strong>, en
    autentisert krypteringsalgoritme — som betyr at ciphertekst som er tuklet
    med, ikke kan dekrypteres uten at det oppdages.
  </p>

  <h3>Passordet ditt forlater aldri nettleseren</h3>
  <p>
    Når du logger inn, sender vi <strong>ikke</strong> passordet til serveren.
    Nettleseren utleder en separat verifikator fra passordet (Argon2id med
    et eget salt, distinkt fra det som brukes til å pakke DEK-en) og sender
    bare denne. Serveren kjører <code>Bun.password.verify</code> mot en hash
    vi har lagret. Om databasen lekker, må en angriper både brute-force-knekke
    verifikatoren <em>og</em> så gjøre samme treghet om igjen for å utlede
    nøkkelen som åpner pakka — to separate angrep, ikke ett.
  </p>

  <h3>Å bytte passord eller bruke gjenopprettingskoden</h3>
  <p>
    Bytter du passord, blir innholdet i den private listen din <em>aldri</em>
    kryptert på nytt. Nettleseren låser bare opp DEK-en med det gamle
    passordet og pakker den inn igjen med det nye. Det går raskt og er
    sikkert: selve dataene rører vi aldri. Gjenopprettingskoden virker etter
    samme prinsipp; den åpner sin egen pakke og lar deg sette et nytt
    passord uten å miste noe.
  </p>
  <p>
    Når noen fullfører gjenoppretting, sjekker serveren først at avsenderen
    faktisk kan gjenopprettingskoden (via en egen verifikator). Det betyr at
    noen som bare kjenner eposten din, ikke kan låse deg ute fra kontoen.
  </p>

  <h3>Hva serveren ser, og ikke ser</h3>
  <p>Vi lagrer per bruker:</p>
  <ul>
    <li>Eposten din (brukt som innloggings-id).</li>
    <li>
      Salter og innpakkede nøkler — disse er ikke hemmelige i seg selv; uten
      passordet eller gjenopprettingskoden låser de ingenting opp.
    </li>
    <li>Hash av autentiseringsverifikatoren (ikke passordet — verifikatoren).</li>
    <li>Visningsnavn, brukernavn og rolleflagg, om du har satt dem.</li>
  </ul>
  <p>For private oppføringer lagrer vi <strong>kun</strong>:</p>
  <ul>
    <li>Ciphertekst (ulesbar uten DEK-en).</li>
    <li>En tilfeldig nonce per oppføring.</li>
    <li>Tidspunkt for opprettelse og siste endring.</li>
  </ul>
  <p>
    Vi lagrer aldri: råpassordet, gjenopprettingskoden, DEK-en i lesbar form,
    eller klartekst-innholdet i private oppføringer.
  </p>

  <h3>Sesjoner og innlogging</h3>
  <p>
    Innlogging gir deg en httpOnly-cookie med en tilfeldig sesjonsverdi som
    lagres i databasen vår. Vi bruker bevisst <em>ikke</em> JWT — ingen
    kompliserte tokens som vi ikke kan trekke tilbake. Logger du ut, eller
    fullfører gjenoppretting, slettes sesjonene umiddelbart.
  </p>

  <h3>Hva som <em>ikke</em> er beskyttet</h3>
  <p>
    Krypteringen beskytter <strong>innholdet</strong> i de private
    oppføringene dine. Den beskytter <strong>ikke</strong> mot:
  </p>
  <ul>
    <li>
      At noen får tilgang til nettleseren din mens du er innlogget. Da har de
      DEK-en i minnet og kan lese alt. Logg ut når du forlater enheten.
    </li>
    <li>
      Trafikkanalyse — antallet og størrelsen på dine private oppføringer er
      synlige for noen som overvåker forbindelsen, selv om innholdet ikke er
      det.
    </li>
    <li>
      Skadelige nettleserutvidelser eller sårbarheter i nettleseren. Ingen
      ende-til-ende-app kan beskytte mot at klienten kompromitteres.
    </li>
  </ul>

  <h3>Vil du grave dypere?</h3>
  <p>
    Den fulle tekniske beskrivelsen ligger i prosjektets <code>SECURITY.md</code>.
    Den dekker eksakt hvilke biter som lagres hvor, hvilke Argon2id-parametre
    vi bruker, og hvordan vi tenker rundt avveiningene.
  </p>
</section>
